import React, { useCallback, useRef, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	CheckboxOption,
	type FormatOptionLabelMeta,
	type InputActionMeta,
	type OptionType,
	PopupSelect,
	type PopupSelectProps,
} from '@atlaskit/select';

import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
	// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
} from '../../../../controllers/quick-find/analytics';
import { onFilterSelection } from '../../../utils/filters/events';
import { Control } from '../../filters/select-filter/control';
import {
	MenuListWithClearFilterOptionLabel,
	MenuListWithoutClearFilterOptionLabel,
} from '../../filters/select-filter/menu-list';

import { instanceOfPopupSelectOption } from './utils';

type Options = OptionType[] | { label: string; options: OptionType[] }[];

export type SelectFilterOptionMetadata = { [key: string]: any };

export type PopupSelectOption = {
	isSelected: boolean;
	trackingKey: string;
	value: string;
	queryParamValue: string;
	isSuggested: boolean;
	isDefault: boolean;
	avatarUrl?: string;
	label: string;
	metadata?: SelectFilterOptionMetadata;
	product?: string;
	isFiltersDiscoverability2Enabled?: boolean;
};

type FilterPopupSelectProps = {
	defaultIsOpen?: boolean;
	isSearchable?: boolean;
	isLoading?: boolean;
	canClearFilter?: boolean;
	checkboxIndicators?: boolean;
	formatOptionLabel?: (
		data: OptionType,
		formatOptionLabelMeta: FormatOptionLabelMeta<OptionType>,
	) => React.ReactNode;
	placeholder?: string;
	searchInput?: string;
	onSearchInputChange?: (value: string) => void;
	onSelectChange: (value: PopupSelectOption | PopupSelectOption[]) => void;
	onDropdownOpen?: () => void;
	onDropdownClose?: () => void;
	options: Options;
	selectedOptions: PopupSelectOption[];
	label: string;
	noOptionsMessage?: string;
	getFirstSelectedLabel?: (selected: OptionType) => string;
	onClearOptions?: () => void;
	minMenuWidth?: number | string;
	testId?: string;
	target?: PopupSelectProps['target'];
	isFiltersDiscoverability2Enabled?: boolean;
	filterId: string;
	filterOption?: (() => boolean) | null;
};

export const FilterPopupSelect = ({
	defaultIsOpen = false,
	isSearchable = false,
	isLoading = false,
	canClearFilter = false,
	checkboxIndicators = false,
	formatOptionLabel,
	placeholder,
	searchInput,
	onSearchInputChange,
	onSelectChange,
	onDropdownOpen,
	onDropdownClose,
	options,
	label,
	noOptionsMessage,
	onClearOptions,
	minMenuWidth,
	target,
	isFiltersDiscoverability2Enabled,
	filterId,
	filterOption: filterOptionOverride,
}: FilterPopupSelectProps): React.JSX.Element => {
	const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	// TODO: this can be removed if we have debounce in the store when lookup
	const [currentSearchInput, setCurrentSearchInput] = useState(searchInput);

	// For now, the FilterPopupSelect component is only being used in Quick Find so the following
	// analytics hooks are valid. Please extract out the analytics events when we reuse this
	// component in FPS.
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes } = useQuickFindAttributes();

	const handleInputChange = useCallback(
		(value: string, action: InputActionMeta) => {
			if (action.action !== 'input-change') {
				return;
			}
			setCurrentSearchInput(value);

			if (!onSearchInputChange) {
				return;
			}

			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}

			debounceTimeout.current = setTimeout(() => {
				onSearchInputChange(value);
			}, 300);
		},
		[onSearchInputChange],
	);

	const handleChange = useCallback(
		(value: OptionType | readonly OptionType[]) => {
			if (!instanceOfPopupSelectOption(value)) {
				throw new Error('Value should match the PopupSelectOption type');
			}

			onSelectChange(value);

			// Omitting these attrtibutes as there is a race condition here since the filter attributes
			// have not yet been applied in the store so it will always be one update behind.
			const {
				productSelected: _productSelected,
				filters: _filters,
				filterSelections: _filterSelections,
				...restAttributes
			} = commonAttributes;
			const selection = (Array.isArray(value) ? value : [value]).at(0);

			if (!selection) {
				return; // This should never be executed
			}

			fireAnalyticsEvent(
				onFilterSelection({
					action: selection.isSelected ? 'unselected' : 'selected',
					location: 'inline',
					actionSubjectId: 'searchDialogFilter',
					attributes: {
						...restAttributes,
						filterId,
					},
				}),
			);
		},
		[commonAttributes, filterId, fireAnalyticsEvent, onSelectChange],
	);

	// Disable react-select's default client-side filtering so server results (e.g. matched by
	// project key) are not incorrectly filtered out because their label doesn't match the input.
	// Consumers can override this by passing filterOption={null} to preserve client-side filtering
	const filterOption =
		filterOptionOverride !== undefined
			? (filterOptionOverride ?? undefined)
			: isSearchable
				? () => true
				: undefined;

	return (
		<PopupSelect
			blurInputOnSelect={false}
			canClearFilter={canClearFilter}
			closeMenuOnSelect={!checkboxIndicators}
			components={{
				Control,
				MenuList: isFiltersDiscoverability2Enabled
					? MenuListWithoutClearFilterOptionLabel
					: MenuListWithClearFilterOptionLabel,
				...(checkboxIndicators ? { Option: CheckboxOption } : {}),
			}}
			defaultIsOpen={defaultIsOpen}
			// @ts-ignore - https://product-fabric.atlassian.net/browse/DSP-21000
			defaultOptions={false}
			formatOptionLabel={formatOptionLabel}
			inputValue={currentSearchInput}
			isClearable={false}
			isLoading={isLoading}
			isOptionSelected={(option) => option.isSelected}
			isSearchable={isSearchable}
			label={label}
			maxMenuHeight={400}
			maxMenuWidth="22rem"
			menuPlacement="bottom"
			minMenuWidth={minMenuWidth}
			noOptionsMessage={currentSearchInput ? undefined : () => noOptionsMessage}
			onChange={handleChange}
			onClearOptions={onClearOptions}
			onInputChange={handleInputChange}
			onMenuClose={() => {
				setCurrentSearchInput('');
				onDropdownClose?.();
			}}
			onMenuOpen={onDropdownOpen}
			options={options}
			placeholder={placeholder}
			popperProps={{
				strategy: 'fixed',
				modifiers: [
					{ name: 'offset', options: { offset: [0, 8] } },
					{
						name: 'preventOverflow',
						enabled: false,
					},
				],
			}}
			searchThreshold={-1}
			shouldCloseMenuOnTab={false}
			target={target}
			{...(fg('search_filter_disable_client_side_filtering') ? { filterOption } : {})}
		/>
	);
};
